// COP-skallet: topplinje, lag-sidebar, kartcontainer og detaljpanel — bygget i
// GNÅ-design (theme.css). Ren DOM, ingen rammeverk (holder klienten lett og
// AI-vedlikeholdbar). Eier ikke data — main.ts wirer feed/kart/RBAC inn.
import { BILDE_EXTRA_NOKKEL, formatMgrs, PLAUSIBILITET_NOKKEL, type Track } from '@skade/contracts';
import { can, type User } from '@skade/rbac';
import { roleLabel } from '../session.js';
import { initEmcon, isEmcon, setEmcon, haptic, HAPTIC } from './feedback.js';
import {
  alderTekst, sorterHendelser, HENDELSE_ETIKETT, hasteBand, hastebandTekst,
  hastegradKlasse, type Hendelse,
} from './hendelseslogg.js';
import { PRECEDENCE_CODEWORD } from '@skade/contracts';
import {
  filtrerHendelser, filterSammendrag, linseTelling, LINSER, LINSE_ETIKETT,
  TOMT_FILTER, erAktivt, type FeltnettFilter, type FeltnettLinse,
} from './feltnett.js';
import { icon } from './icons.js';

export interface LayerDef {
  id: 'sensors' | 'targets';
  name: string;
  color: string;
}

/** Én rad i den levende objektlisten (høyre panel). */
export interface RosterItem {
  id: string;
  label: string;
  sub: string;
  /** Tilhørighet for fargeprikk (friend/hostile/neutral/unknown/sensor). */
  aff: string;
  /** Eksplisitt prikkfarge (overstyrer aff, f.eks. meldingstype-farge). */
  color?: string;
  /**
   * Ekstra søkeord som IKKE vises i raden.
   *
   * Finnes for GNÅ-brukere: eieren vil se navnet og ikke «gna4791994407» i
   * lista, men et telefonnummer eller en Matrix-id lest opp over radio må
   * fortsatt kunne søkes opp. Skjult tekst i søket løser begge, uten å fylle
   * raden med maskin-id-er.
   */
  sok?: string;
  /**
   * Gruppeoverskrift raden hører under («Enheter», «Påtegninger»).
   *
   * Finnes fordi lista nå viser ALT som ligger i kartet, ikke bare spor og
   * enheter (UX-rapporten 2026-08-14: 21 artefakter, 0 i lista). En flat liste
   * av 21 typer er ikke oversikt, den er en haug. Uten `gruppe` tegnes raden
   * uten overskrift — meldingslista bruker den ikke.
   */
  gruppe?: string;
  /**
   * Fjern-vei for DENNE raden. Får papirkorg-ikon ved hover, aldri `×`.
   *
   * ⚠ `×` betyr LUKK, alltid (rapportens §4b: samme MapLibre-popup-`×` SLETTET
   * en måling og SKJULTE et enhetskort, så operatøren kunne ikke lære hva
   * krysset gjorde — hun måtte huske hvilket verktøy hun sto i).
   */
  onSlett?: () => void;
  /** Tittel på slette-knappen. Skal navngi objektet, ikke bare si «Slett». */
  slettTittel?: string;
  onClick: () => void;
}

/**
 * Filtrer objektlista på fritekst. Ren funksjon → testbar uten DOM.
 *
 * Regelen: alle ordene i søket må finnes (OG), hvert ord kan treffe navn,
 * undertittel ELLER de skjulte søkeordene. Ord-for-ord er valgt fordi
 * operatøren skriver «ola tropp2», ikke en sammenhengende delstreng — og
 * ubeskyttet delstrengsøk på hele frasen ville gitt null treff der.
 */
export function filtrerRoster(items: RosterItem[], q: string): RosterItem[] {
  const ord = (q ?? '').toLowerCase().split(/\s+/).filter(Boolean);
  if (!ord.length) return items;
  return items.filter((it) => {
    const høystakk = `${it.label} ${it.sub} ${it.sok ?? ''}`.toLowerCase();
    return ord.every((o) => høystakk.includes(o));
  });
}

/**
 * Bygg-identitet, satt ved BYGGETID av Vite fra `VITE_BUILD_ID` (Docker-bygget
 * mater inn git-SHA + tidspunkt). Uten den — altså i `pnpm dev` — står det
 * «dev». Vises nederst til venstre, så en deploy kan verifiseres visuelt.
 */
export const BUILD_ID: string =
  (import.meta.env?.VITE_BUILD_ID as string | undefined)?.trim() || 'dev';

/**
 * Nedre rad i MGRS-kortet nederst til høyre. EGEN posisjon står alltid øverst;
 * denne raden viser MUS til vanlig, men bytter til TRÅDKORS når sikte er
 * aktivert — for da er trådkorset (kartsenteret) punktet operatøren leser av,
 * ikke musepekeren. Etiketten byttes sammen med verdien, så tallet aldri kan
 * misforstås som den andre kilden. Ren funksjon → testbar uten DOM.
 */
export function lowerCoordRow(
  crosshairOn: boolean,
  mouseMgrs: string,
  crosshairMgrs: string,
): { label: string; value: string } {
  return crosshairOn
    ? { label: 'TRÅDKORS', value: crosshairMgrs || '—' }
    : { label: 'MUS', value: mouseMgrs || '—' };
}

/**
 * Radene i spor-detaljpanelet, i visningsrekkefølge. Ren funksjon → testbar uten
 * DOM, samme grunn som `lowerCoordRow` over.
 *
 * REKKEFØLGEN ER KRAVET, ikke innholdet. Eierfunn 2026-08-17: COP-en tegnet et
 * vennlig bakkespor på 37.78583, −122.40642 — iOS-simulatorens standardkoordinat
 * — med «CEP 5 m» og «3 min gammel», og sa ikke fra med ett ord. Transporten var
 * frisk; det som manglet var at bildet sa at det ikke trodde på punktet.
 *
 * Plausibilitetsforbeholdet plukkes derfor UT av `extra` (som rendres helt
 * nederst, etter Konfidens og CEP) og settes rett under posisjonen det gjelder.
 * En advarsel om et tall må stå ved tallet: står den to rader under en «CEP 5 m»
 * som ser presis ut, leses presisjonen først og forbeholdet kanskje aldri.
 */
export function sporRader(
  t: Track,
  naa: number,
  gammelt: boolean,
): Array<[string, string]> {
  const rader: Array<[string, string]> = [
    ['Kilde', t.source],
    ['Posisjon', `${t.lat.toFixed(5)}, ${t.lon.toFixed(5)}`],
  ];
  const uplausibelt = t.extra?.[PLAUSIBILITET_NOKKEL];
  if (uplausibelt) rader.push([PLAUSIBILITET_NOKKEL, uplausibelt]);
  rader.push(
    ['SIDC', t.sidc],
    // ALDER, ikke bare klokkeslett. Et Zulu-tidspunkt alene krever hoderegning
    // i en situasjon der ingen har tid til det — og for et bevart spor er
    // nettopp alderen hele poenget.
    [gammelt && t.bevares ? 'Sist sett' : 'Sett',
      `${new Date(t.tsMs).toISOString().slice(11, 19)}Z · ${alderTekst(naa, t.tsMs)} gammel`],
  );
  if (t.confidence !== undefined) rader.push(['Konfidens', `${Math.round(t.confidence * 100)} %`]);
  if (t.cepM !== undefined) rader.push(['CEP', `${Math.round(t.cepM)} m`]);
  // Forbeholdet er alt rendret over; uten dette hoppet står det TO ganger.
  for (const [k, v] of Object.entries(t.extra ?? {})) {
    if (k === PLAUSIBILITET_NOKKEL) continue;
    // ⚠ BILDET SKAL IKKE VÆRE EN TEKSTRAD. Det rendres som et faktisk <img>
    // nedenfor radene (se `showDetail`). Uten dette hoppet sto observasjonen som
    // strengen «Bilde: /media/a3f9….jpg» — og det var nøyaktig feilen: bildet kom
    // helt fram til operatøren og ble så vist som en URL (eierfunn 2026-08-17,
    // meldt av meg som «ferdig» i bølge 194–195. Det var det ikke).
    if (k === BILDE_EXTRA_NOKKEL) continue;
    rader.push([cap(k), v]);
  }
  return rader;
}

/**
 * Tillatte bilde-URL-er i detaljpanelet. Speiler `safeImageUrl` i `ui/imint.ts`.
 *
 * `^/(?!/)` er samme opphav (vårt eget `/media/…`) og kan ikke beacone noe sted.
 * To skråstreker er PROTOKOLL-RELATIV, altså kryssopphav, og avvises — et delt
 * intel-produkt skal ikke kunne fortelle en angriper hvem som åpnet det og når.
 */
export function trygtBildeUrl(url: string): string {
  const u = (url || '').trim();
  const trygg =
    /^https:\/\//i.test(u) || /^data:image\//i.test(u) || /^\/(?!\/)/.test(u);
  return trygg ? u : '';
}

/**
 * Tråkorset huskes mellom økter. Nøkkelen er bevisst spesifikk, så den ikke
 * kolliderer med andre kartvalg.
 *
 * DEFAULT ER PÅ: mangler nøkkelen (første gang, eller privat vindu), skal
 * tråkorset stå på. Bare et eksplisitt lagret `'0'` slår det av — altså må
 * operatøren ha valgt det selv. `localStorage` kan kaste i låste kontekster,
 * så begge veier er beskyttet; feiler lagringen, mister vi bare hukommelsen,
 * ikke funksjonen.
 */
const TRAKORS_NOKKEL = 'skade.kart.trakors';

export function lesTrakorsValg(): boolean {
  try { return localStorage.getItem(TRAKORS_NOKKEL) !== '0'; } catch { return true; }
}

function skrivTrakorsValg(on: boolean): void {
  try { localStorage.setItem(TRAKORS_NOKKEL, on ? '1' : '0'); } catch { /* låst lagring */ }
}

/**
 * Hvor lenge 112-knappen må holdes inne før nødalarmen kringkastes.
 *
 * 2000 ms er valgt fordi det er lengre enn ETHVERT utilsiktet trykk (et streif,
 * en dobbeltklikk-rest, en tab+mellomrom fra feil fokus) og kort nok til at det
 * ikke føles som motstand når nøden er ekte. Går den under ~1 s begynner uhell
 * å slippe gjennom; over ~3 s begynner operatøren å tro at knappen er ødelagt.
 */
export const EM_HOLD_MS = 2000;

// ── Venstre sidepanel: kollapsbart (revisjonsfunn 24, «døde soner») ─────────
//
// Panelet har innhold bare i øverste ~300 px — kartlag, RBAC-lesning og
// meldingslista. Resten er tom flate som spiser 264 px fra kartet i all evighet.
// Kartet er helten (UX §2), så operatøren skal kunne gi det plassen tilbake med
// ÉN knapp, og valget skal huskes: en operatør som jobber i kartet hele vakta
// skal ikke måtte kollapse panelet på nytt hver gang klienten lastes.
// MERK: høyrepanelet røres IKKE her — det er en større ombygging (hendelseslogg).

/** localStorage-nøkkel for om venstre sidepanel er kollapset. */
export const SIDEPANEL_NOKKEL = 'skade.sidepanel.skjult';

/** Minste lager-flate vi trenger — gjør lesing/skriving testbar uten nettleser. */
export interface Lager {
  getItem(k: string): string | null;
  setItem(k: string, v: string): void;
}

/** Leser lagret tilstand. Ukjent/ødelagt verdi ⇒ panelet er SYNLIG: en tom
 *  localStorage skal aldri kunne skjule innhold operatøren ikke har bedt om. */
export function lesSidepanelSkjult(lager?: Lager | null): boolean {
  try { return lager?.getItem(SIDEPANEL_NOKKEL) === '1'; } catch { return false; }
}

/** Lagrer tilstanden. Svelger feil (privat modus / full kvote) — å ikke kunne
 *  HUSKE valget skal ikke hindre operatøren fra å TA det. */
export function skrivSidepanelSkjult(lager: Lager | null | undefined, skjult: boolean) {
  try { lager?.setItem(SIDEPANEL_NOKKEL, skjult ? '1' : '0'); } catch { /* ignorer */ }
}

export class Shell {
  /** Settes av main: popp objektlista ut til eget vindu/skjerm. */
  onPopoutRoster?: () => void;
  /** Settes av main: åpne AAR-panelet (AAR-01 — generer fra hendelsesloggen). */
  onGenererAar?: () => void;
  /** Settes av main: åpne stridsmelding-komponisten. */
  onNewReport?: () => void;
  /** Settes av main: popp C2-panelet ut til eget vindu. */
  onPopoutC2?: () => void;
  /** Settes av main: slå av/på én-skjerm-modus (nettbrett/laptop). */
  onSingleScreen?: (on: boolean) => void;
  /** Settes av main: nåværende én-skjerm-tilstand. */
  getSingleScreen?: () => boolean;
  readonly mapEl: HTMLElement;
  private zuluEl!: HTMLElement;
  private localEl!: HTMLElement;
  private detailEl!: HTMLElement;
  private rosterEl!: HTMLElement;
  private rosterSokEl!: HTMLInputElement;
  /** Alle objekter, ufiltrert — søket filtrerer en kopi ved tegning. */
  private rosterItems: RosterItem[] = [];
  private rosterQ = '';
  /** Speiler seleksjonseierens tilstand — lista har ikke sin egen mening. */
  private rosterValgt: string | null = null;
  private hendelseEl!: HTMLElement;
  private hendelseCountEl!: HTMLElement;
  private hasteEl!: HTMLElement;
  private linseEl!: HTMLElement;
  private filterTekstEl!: HTMLElement;
  private sokEl!: HTMLInputElement;
  /** Aktivt filter. Holdes HER fordi lista tegnes her — én kilde (§3). */
  private feltnett: FeltnettFilter = { ...TOMT_FILTER };
  /** Siste sett hendelser + tid, så et filterbytte kan tegne på nytt uten data. */
  private sisteHendelser: Hendelse[] = [];
  private sisteNaa = 0;
  private rosterCountEl!: HTMLElement;
  private reportsEl!: HTMLElement;
  private alarmEl!: HTMLElement;
  private tilbudEl!: HTMLElement;
  private tilbudTimer: number | null = null;
  private coordEl!: HTMLElement;
  private crosshairEl!: HTMLElement;
  private counts: Record<string, HTMLElement> = {};
  private layerState: Record<string, boolean> = { sensors: true, targets: true };

  constructor(
    root: HTMLElement,
    private user: User,
    private layers: LayerDef[],
    private onToggle: (id: LayerDef['id'], on: boolean) => void,
    private onGoto: () => void,
    private demo = false,
    private onEmergency: (active: boolean) => void = () => {},
    private onLogout?: () => void,
    private canAdmin = false,
    private onAdmin?: () => void,
    private onCycleMode?: () => void,
  ) {
    root.innerHTML = '';
    root.className = 'skade-shell';
    this.rootEl = root;
    initEmcon(); // gjenopprett lys-/lyddisiplin fra lagret tilstand
    root.appendChild(this.header());
    root.appendChild(this.sidebar());
    this.mapEl = this.mapArea();
    root.appendChild(this.mapEl);
    root.appendChild(this.detail());
    // Gjenopprett lagret panel-tilstand FØR første maling, ellers blinker
    // panelet inn og ut ved hver oppstart.
    this.settSidepanelSkjult(lesSidepanelSkjult(this.lager()), false);
    this.tickClock();
    setInterval(() => this.tickClock(), 1000);
  }

  // ── Venstre sidepanel av/på ────────────────────────────────────────────────
  private rootEl!: HTMLElement;
  private sidepanelBtn?: HTMLButtonElement;

  /** localStorage når den finnes (mangler i tester og noen kiosk-webviews). */
  private lager(): Lager | null {
    try { return typeof localStorage === 'undefined' ? null : localStorage; } catch { return null; }
  }

  /** Er venstre sidepanel skjult nå? */
  sidepanelSkjult(): boolean {
    return this.rootEl.classList.contains('sidepanel-skjult');
  }

  /**
   * Skjuler/viser venstre sidepanel. Kartet utvider seg i samme rutenett;
   * MapLibre følger med av seg selv (ResizeObserver på kartcontaineren), så
   * ingen manuell `resize()` er nødvendig.
   */
  settSidepanelSkjult(skjult: boolean, lagre = true) {
    this.rootEl.classList.toggle('sidepanel-skjult', skjult);
    if (this.sidepanelBtn) {
      // Knappen forteller TILSTANDEN, ikke bare handlingen — en bryter som ikke
      // sier hva den er, må prøves for å forstås.
      this.sidepanelBtn.classList.toggle('active', !skjult);
      this.sidepanelBtn.setAttribute('aria-pressed', String(!skjult));
      this.sidepanelBtn.title = skjult
        ? 'Vis venstre panel (kartlag, tilgang, stridsmeldinger)'
        : 'Skjul venstre panel — kartet får plassen';
    }
    if (lagre) skrivSidepanelSkjult(this.lager(), skjult);
  }

  private sidepanelKnapp(): HTMLButtonElement {
    const b = el('button', 'hdr-btn sidepanel-btn active') as HTMLButtonElement;
    b.type = 'button';
    b.setAttribute('aria-label', 'Venstre panel av/på');
    b.innerHTML = icon('sidepanel'); // klarert konstant fra icons.ts (ingen feltdata)
    b.onclick = () => this.settSidepanelSkjult(!this.sidepanelSkjult());
    this.sidepanelBtn = b;
    return b;
  }

  private header(): HTMLElement {
    const h = el('header', 'skade-header');

    // Venstre: panelbryter + merke. Bryteren står rett over panelet den styrer,
    // i topplinja der den aldri dekker kartet og aldri forsvinner med panelet.
    const left = el('div', 'hdr-left');
    left.append(this.sidepanelKnapp(), span('brand', 'SKADE'), span('sub', 'FELLES SITUASJONSBILDE'));
    if (this.demo) left.append(span('demo-chip', 'DEMO — IKKE EKTE DATA'));
    h.append(left);

    // Midten: stor Zulu + lokal tid (posisjon).
    const center = el('div', 'hdr-clock');
    const zwrap = el('div', 'clock-col');
    zwrap.append(span('clock-lbl', 'ZULU'), (this.zuluEl = span('clock-big', '')));
    const lwrap = el('div', 'clock-col');
    lwrap.append(span('clock-lbl', 'LOKAL'), (this.localEl = span('clock-big local', '')));
    center.append(zwrap, lwrap);
    h.append(center);

    // Høyre: 112 + kontomeny (én meny, se `kontoMeny`).
    // 112 er en NØDKONTROLL og skal ikke lese som en nabo av kontovelgeren —
    // en sikkerhetskritisk knapp vegg-i-vegg med en rutinemeny øker risikoen
    // for feilklikk (UX-rapport J3 2026-08-04, funn 4). Skilleveggen gir
    // synlig luft og et eget «farlig sone»-felt før kontomenyen.
    const right = el('div', 'hdr-right');
    right.append(this.emButton());
    right.append(el('div', 'hdr-skille'));
    right.append(this.kontoMeny());
    h.append(right);
    return h;
  }

  /**
   * KONTOMENY — profil, innstillinger, administrasjon og utlogging i ÉN meny.
   *
   * ⚠ HVA SOM GIKK GALT: her sto det to nedtrekk side om side. «Admin» hadde
   * ETT punkt («Inviter bruker»), og nabomenyen — den som eier kontoinnstillinger
   * og utlogging — var navngitt etter brukerens ORGANISASJONSENHET (rolle +
   * distrikt, f.eks. «FOH-NASJONAL»). Resultatet var at operatøren måtte gjette:
   * to menyer som begge kunne inneholde «innstillinger», og den ene het noe som
   * så ut som en avdeling, ikke som «meg». To naboer som konkurrerer om samme
   * mentale rubrikk er verre enn én meny med et punkt for mye.
   *
   * Så lenge administrasjon er ETT punkt, hører det hjemme her: det er noe DENNE
   * brukeren har rett til å gjøre, ikke et eget arbeidsområde. Vokser admin til
   * flere punkter (roller, org-treet, revisjonslogg) skal det skilles ut igjen —
   * men da som et PANEL med egen plass, ikke som et nedtrekk med ett punkt.
   *
   * Menyen bærer nå NAVNET sitt («Konto») i knappen, med identiteten etter, så
   * det aldri leses som en organisasjonsenhet. Rolle + distrikt står på den
   * sekundære linja, der de hører — de opplyser hvem du er, de navngir ikke menyen.
   */
  private kontoMeny(): HTMLElement {
    const trigger = el('span', 'user-trigger');
    // Klarert konstant fra icons.ts (ren SVG). Person-ikonet er det raskeste
    // signalet om at dette er «meg», ikke en avdeling — jf. UX-regel 6 (ikon,
    // ikke emoji).
    const ikon = el('span', 'user-glyph');
    ikon.innerHTML = icon('contacts');
    const linjer = el('span', 'user-lines');
    linjer.append(
      span('user-name', `Konto · ${shortId(this.user.id)}`),
      span('user-role', roleLabel(this.user)),
    );
    trigger.append(ikon, linjer);

    const items: MenuItem[] = [
      { label: 'Profil', glyph: '👤', run: () => this.openProfile() },
      { label: 'Brukerinnstillinger', glyph: '⚙', run: () => this.openSettings() },
    ];
    // Administrasjon: bare for roller som faktisk har retten (RBAC avgjør,
    // ikke menyen — klient-gating er aldri nok alene, grunnloven §4).
    if (this.canAdmin && this.onAdmin) {
      items.push({ label: 'Administrasjon — inviter bruker', glyph: '＋', run: () => this.onAdmin?.() });
    }
    if (this.onLogout) items.push({ label: 'Logg ut', glyph: '⏻', run: () => this.onLogout?.(), danger: true });
    const meny = dropdown('hdr-btn user-btn', trigger, items);
    meny.querySelector('button')?.setAttribute(
      'title', 'Konto — profil, brukerinnstillinger'
      + `${this.canAdmin && this.onAdmin ? ', administrasjon' : ''} og utlogging`,
    );
    return meny;
  }

  private openProfile() {
    const o = this.user.org;
    modal('Profil', [
      ['Bruker-id', this.user.id],
      ['Rolle', roleLabel(this.user)],
      ['Distrikt', o.district || '—'],
      ['Område', o.area || '—'],
      ['Tropp', o.troop || '—'],
      ['Lag', o.team || '—'],
    ]);
  }

  private openSettings() {
    const body = el('div', 'modal-body');
    body.append(span('modal-row-label', 'Kartmodus'));
    const modeBtn = el('button', 'report-submit') as HTMLButtonElement;
    modeBtn.textContent = 'Bytt dag / natt / NVG';
    modeBtn.onclick = () => this.onCycleMode?.();
    body.append(modeBtn);

    // Én-skjerm-modus (nettbrett/laptop): ett app-vindu om gangen, fullskjerm,
    // så panelene ikke stables oppå kartet. På desktop: flyttbare vinduer.
    if (this.onSingleScreen) {
      body.append(span('modal-row-label', 'Visning'));
      const row = el('label', 'calendar-filter');
      const cb = el('input') as HTMLInputElement;
      cb.type = 'checkbox'; cb.checked = this.getSingleScreen?.() ?? false;
      cb.onchange = () => this.onSingleScreen?.(cb.checked);
      row.append(cb, document.createTextNode(' Én-skjerm-modus (nettbrett/laptop) — ett vindu om gangen, fullskjerm'));
      body.append(row);
      body.append(span('checklist-note', 'Av: apper åpnes som flyttbare vinduer du kan dra i tittellinja og skalere.'));
    }

    // EMCON — lys- og lyddisiplin: demper vibrasjon + skjerm-animasjon i felt.
    body.append(span('modal-row-label', 'Felt-disiplin'));
    const emconRow = el('label', 'calendar-filter');
    const emconCb = el('input') as HTMLInputElement;
    emconCb.type = 'checkbox'; emconCb.checked = isEmcon();
    emconCb.onchange = () => setEmcon(emconCb.checked);
    emconRow.append(emconCb, document.createTextNode(' EMCON — lydløs (ingen vibrasjon) + dempet lys/animasjon'));
    body.append(emconRow);
    body.append(span('checklist-note', 'Flere innstillinger kommer (varsler, språk, tema).'));
    modalEl('Brukerinnstillinger', body);
  }

  private _emBtn!: HTMLButtonElement;
  private _emActive = false;
  private _emHoldTimer: number | null = null;
  private _emHeld = false;

  /**
   * 112-nødknapp.
   *
   * ⚠ ASYMMETRISK MED VILJE: Å SENDE krever at knappen holdes inne i
   * `EM_HOLD_MS`; å AVLYSE er ett klikk.
   *
   * Før dette var utsendelse ett enkelt klikk i topplinja. Det kringkaster egen
   * posisjon til HELE nettet — en handling som ikke kan trekkes tilbake, bare
   * etterfølges av en avlysning som alle allerede har sett. Et streif med
   * musa, en feiltrykk på en berøringsskjerm i en bil, eller tab+mellomrom fra
   * en operatør som trodde fokus lå et annet sted, var nok. Avlys-muligheten
   * var det ENESTE sikkerhetsnettet; nå er den nett nummer to.
   *
   * Motsatt vei skal det ALDRI være friksjon: er alarmen sendt ved uhell, eller
   * er nøden over, skal den kunne stoppes med det samme. Derfor ingen hold på
   * avlysning.
   */
  private emButton(): HTMLElement {
    const b = el('button', 'em-btn') as HTMLButtonElement;
    b.type = 'button';
    this._emBtn = b;
    this.emLabel();

    // Pekerbaserte hold. `pointerdown` dekker mus, penn og finger med én kode.
    b.addEventListener('pointerdown', (e) => {
      // Bare primærknapp; høyreklikk skal ikke starte en nødsending.
      if (e.button !== 0) return;
      this.emHoldStart();
    });
    // Alle veier ut av holdet MÅ avbryte. Uten `pointercancel`/`pointerleave`
    // ville en timer kunne fyre etter at fingeren forlot knappen.
    for (const ev of ['pointerup', 'pointercancel', 'pointerleave', 'blur'] as const) {
      b.addEventListener(ev, () => this.emHoldStop());
    }

    // Tastatur: en knapp MÅ kunne betjenes uten peker. Standardoppførselen for
    // <button> er å utløse `click` (mellomrom på keyup, Enter på keydown) — den
    // stopper vi, ellers ville tastaturbruk omgå hele bekreftelsen.
    b.addEventListener('keydown', (e) => {
      if (e.key !== ' ' && e.key !== 'Enter') return;
      e.preventDefault();
      if (e.repeat) return;
      this.emHoldStart();
    });
    b.addEventListener('keyup', (e) => {
      if (e.key !== ' ' && e.key !== 'Enter') return;
      e.preventDefault();
      this.emHoldStop();
    });

    // Klikk brukes KUN til avlysning. Sending går utelukkende via hold.
    b.addEventListener('click', (e) => {
      e.preventDefault();
      if (this._emActive) this.onEmergency(false);
    });

    return b;
  }

  /**
   * Starter (eller fortsetter) et hold. Er alarmen allerede sendt, gjør holdet
   * ingenting — da er knappen en avlysningsknapp, og klikket håndterer den.
   */
  private emHoldStart() {
    if (this._emActive || this._emHoldTimer !== null) return;
    this._emHeld = true;
    this._emBtn.classList.add('holding');
    this._emBtn.style.setProperty('--em-hold-ms', `${EM_HOLD_MS}ms`);
    this.emLabel();
    this._emHoldTimer = window.setTimeout(() => {
      this._emHoldTimer = null;
      this._emHeld = false;
      this._emBtn.classList.remove('holding');
      haptic(HAPTIC.emergency);
      this.onEmergency(true);
    }, EM_HOLD_MS);
  }

  /** Slipp før tida er ute = avbrutt. Sier det høyt: stillhet leses som «sendt». */
  private emHoldStop() {
    const varMidtIHold = this._emHoldTimer !== null;
    if (this._emHoldTimer !== null) { clearTimeout(this._emHoldTimer); this._emHoldTimer = null; }
    if (!this._emHeld) return;
    this._emHeld = false;
    this._emBtn.classList.remove('holding');
    this.emLabel();
    if (varMidtIHold) this.showAlarm(`112 IKKE sendt — hold knappen inne i ${EM_HOLD_MS / 1000} sekunder`);
  }

  /**
   * Én kilde til knappeteksten, så tilstand og etikett ikke kan gli fra hverandre.
   *
   * Teksten ligger i et EGET span og ikke som tekstnode direkte i knappen: hold-
   * fyllingen (`.em-btn.holding::after`) er absolutt posisjonert, og en tekstnode
   * kan ikke løftes over den med z-index. Uten spannet ville fyllingen legge seg
   * OVER tallet «112» i det øyeblikket operatøren trenger å lese det.
   */
  private emLabel() {
    const b = this._emBtn;
    let t = b.querySelector<HTMLElement>('.em-btn-tekst');
    if (!t) { t = span('em-btn-tekst', ''); b.appendChild(t); }
    if (this._emActive) {
      t.textContent = '112 — AVLYS';
      b.title = 'Avlys nødalarmen — ett klikk, ingen bekreftelse';
    } else if (this._emHeld) {
      t.textContent = '112 — HOLD …';
      b.title = 'Slipp for å avbryte';
    } else {
      t.textContent = '112';
      b.title = `Nødalarm — HOLD inne i ${EM_HOLD_MS / 1000} s for å kringkaste posisjon til hele nettet`;
    }
  }

  /** Reflekterer nødtilstand i knappen (også når andre avlyser). */
  setEmergency(active: boolean) {
    this._emActive = active;
    // Et pågående hold skal dø hvis alarmen slås på/av fra en annen kilde —
    // ellers ville timeren kringkaste på nytt like etter en avlysning.
    if (this._emHoldTimer !== null) { clearTimeout(this._emHoldTimer); this._emHoldTimer = null; }
    this._emHeld = false;
    this._emBtn.classList.remove('holding');
    this.emLabel();
    this._emBtn.classList.toggle('active', active);
  }

  private sidebar(): HTMLElement {
    const s = el('aside', 'skade-sidebar');
    s.appendChild(label('Kartlag'));
    for (const l of this.layers) {
      const row = el('div', 'layer-row');
      const dot = el('span', 'dot');
      dot.style.background = l.color;
      const name = span('name', l.name);
      const count = span('count', '0');
      const toggle = el('span', 'toggle');
      this.counts[l.id] = count;
      row.append(dot, name, count, toggle);
      row.onclick = () => {
        this.layerState[l.id] = !this.layerState[l.id];
        row.classList.toggle('off', !this.layerState[l.id]);
        this.onToggle(l.id, this.layerState[l.id]);
      };
      s.appendChild(row);
    }
    // ── Tilgang (RBAC) — INFORMASJON, ikke kontroll ────────────────────────
    // ⚠ HVA SOM GIKK GALT: disse radene brukte `.layer-row`, altså PRESIS samme
    // form som kartlag-bryterne rett over — prikk, navn, verdi til høyre, hover
    // og pekefinger-markør. Operatøren leste dem derfor som av/på-brytere og
    // trykket på dem for å «gi seg selv» eksport eller mållister. Ingenting
    // skjedde. Tilgang utledes av rolle + org og håndheves i backend (grunnloven
    // §4, default deny); den kan ikke slås på fra en sidebar, og skal ikke se ut
    // som den kan. Radene har nå egen form: låseikon i stedet for status-prikk,
    // ingen bryterpille, ingen hover, ingen pekefinger.
    // UX-funn 2026-07-29: seksjonen sto åpen for alle, med systemord som
    // etiketter («RBAC», «CoT/NVG», «Autoriser SPARK»). For sluttbrukeren er
    // det informasjon uten et spørsmål bak seg — den trengs først når noe er
    // nektet og han lurer på hvorfor. Derfor: menneskelige etiketter, og hele
    // blokka sammenleggbar bak «Dine tilganger», LUKKET som standard.
    // Systemordet står igjen i parentes DER det trengs (feilmeldinger fra
    // hubben bruker det), ikke som overskrift.
    const tilgangDetaljer = document.createElement('details');
    tilgangDetaljer.className = 'perm-details';
    const tilgangSammendrag = document.createElement('summary');
    tilgangSammendrag.className = 'section-label perm-summary';
    tilgangSammendrag.textContent = 'Dine tilganger';
    tilgangDetaljer.appendChild(tilgangSammendrag);
    tilgangDetaljer.appendChild(
      span('perm-note', 'Følger rolle og enhet, og kan ikke endres her. Mangler du en tilgang du trenger, er det stab som gir den.'),
    );
    tilgangDetaljer.appendChild(
      permRow('Fjernstyre sensorer (SPARK)', can(this.user, 'authorize', { resource: 'spark_control', org: this.user.org })),
    );
    tilgangDetaljer.appendChild(
      permRow('Eksportere til andre systemer (ATAK)', can(this.user, 'export', { resource: 'export', org: this.user.org })),
    );
    tilgangDetaljer.appendChild(
      permRow('Se mållister', can(this.user, 'read', { resource: 'target_list', org: this.user.org })),
    );
    s.appendChild(tilgangDetaljer);

    // Stridsmeldinger (C2): «＋ Ny melding», pop-out, og levende liste.
    const c2head = el('div', 'roster-head');
    c2head.append(span('section-label', 'Stridsmeldinger'));
    const pop = el('button', 'roster-popout') as HTMLButtonElement;
    pop.textContent = '⧉'; pop.title = 'Åpne stridsmeldinger på egen skjerm';
    pop.onclick = () => this.onPopoutC2?.();
    c2head.appendChild(pop);
    s.appendChild(c2head);
    const nb = el('button', 'new-report-btn') as HTMLButtonElement;
    nb.textContent = '＋ Ny melding';
    nb.onclick = () => this.onNewReport?.();
    s.appendChild(nb);
    this.reportsEl = el('div', 'roster-list');
    s.appendChild(this.reportsEl);

    return s;
  }

  /** Fyller den levende stridsmelding-lista (venstre panel). */
  setReports(items: RosterItem[]) {
    this.reportsEl.innerHTML = '';
    if (!items.length) {
      this.reportsEl.appendChild(span('roster-empty', 'Ingen meldinger'));
      return;
    }
    for (const it of items) this.reportsEl.appendChild(rosterRow(it));
  }

  private mapArea(): HTMLElement {
    const wrap = el('main', 'skade-map');
    const map = el('div');
    map.id = 'map';
    wrap.appendChild(map);
    this.alarmEl = el('div', 'alarm-banner');
    wrap.appendChild(this.alarmEl);
    // Tilbud (se `showTilbud`) — eget element, under alarmstripa, fordi et TILBUD
    // og et VARSEL ikke er samme sak og aldri skal fortrenge hverandre.
    this.tilbudEl = el('div', 'map-tilbud');
    wrap.appendChild(this.tilbudEl);
    // Tråkors (av/på) — sentrert, klikk går gjennom til kartet.
    //
    // STANDARD PÅ. Eier-bestilling: «Tråkorset på», og «kors på sentrering av kart
    // for å finne grid». Å finne griden er en av de mest brukte handlingene i
    // kartet, så den skal ikke kreve at operatøren først finner en bryter. Valget
    // huskes, slik at den som slår det av ikke får det tilbake ved hver last.
    this.crosshairEl = el('div', 'map-crosshair');
    this.crosshairEl.appendChild(el('div', 'hub')); // sentrumsring
    if (lesTrakorsValg()) this.crosshairEl.classList.add('on');
    wrap.appendChild(this.crosshairEl);
    // MGRS-avlesning nederst til høyre: EGEN posisjon alltid øverst. Nedre rad
    // viser MUS — men når trådkorset er aktivt bytter den til TRÅDKORS, fordi
    // det er sikte­punktet operatøren da faktisk leser av (eier-krav). Rad-
    // etiketten byttes sammen med verdien, så det aldri er tvil om hva tallet er.
    this.coordEl = el('div', 'coord-card');
    this.coordEl.innerHTML =
      '<div class="coord-row"><span class="coord-lbl">EGEN</span><span class="mgrs" data-self>—</span></div>' +
      '<div class="coord-row"><span class="coord-lbl" data-lbl2>MUS</span><span class="mgrs" data-mouse>—</span></div>';
    wrap.appendChild(this.coordEl);
    // Versjonsstempel nederst til VENSTRE (coord-kortet eier høyre side). Gjør det
    // mulig å se med ett blikk om en deploy faktisk traff — uten dette er «ser
    // helt likt ut» umulig å skille fra «ikke deployet». Bygg-id settes av
    // Docker-bygget (VITE_BUILD_ID); i utviklingsmodus står det «dev».
    const v = el('div', 'build-badge');
    v.textContent = BUILD_ID;
    v.title = `SKADE COP · bygg ${BUILD_ID}\nKlikk for å kopiere`;
    v.onclick = () => { void navigator.clipboard?.writeText(BUILD_ID); };
    wrap.appendChild(v);
    return wrap;
  }

  private detail(): HTMLElement {
    const d = el('aside', 'skade-detail');
    // ── HENDELSESLOGG øverst ────────────────────────────────────────────────
    // FUNN 06/07/24: en innsendt rapport ble usynlig, og høyrepanelet — den
    // eneste flata som alltid står åpen — sto på «Ingen objekter ennå». Loggen
    // er svaret: siste rapporter, varsler og statusendringer, nyeste først, med
    // ALDER. Ingen ny datakilde, bare ett sted der det som skjer blir synlig.
    const logHead = el('div', 'roster-head');
    // ⚠ HETER «TIDSLINJE», IKKE «FELTNETT» — ENNÅ. Rapportens konseptnavn er
    // Feltnett, og det navnet skal denne flata arve. Men Feltnett er definert som
    // ETT lag der Feltveksler, komposeren, Kontakter og Feltmelding er smeltet
    // sammen, og det er de ikke: de er fortsatt egne paneler. Å sette navnet på
    // loggen nå ville vært samme feil som «Feltmelding / PTT» var — et navn som
    // lover noe koden ikke gjør. «Tidslinje» er sant i dag OG når laget slås
    // sammen (rapporten: «Én tidslinje, filtrerbar»).
    logHead.append(span('section-label', 'Tidslinje'), (this.hendelseCountEl = span('roster-count', '0')));
    // ── UTVID (UX-rapportens Fase 1: «ett blikk gir hele bildet») ──────────
    // Loggen sto på 30 vh, og etter at filterrad, søkefelt og haste-bånd kom til
    // ble det for lite til å SE en time. Men den kan ikke stå stor som standard
    // heller: objektlista og detaljpanelet under er også alltid-synlige flater.
    // Derfor en bryter, og den husker ingenting med vilje — en operatør som tar
    // over stasjonen skal møte samme oppsett hver gang.
    const utvid = el('button', 'logg-utvid');
    utvid.textContent = '⤢';
    utvid.title = 'Utvid tidslinja (mer historikk i ett blikk)';
    (utvid as HTMLButtonElement).onclick = () => {
      const stor = this.hendelseEl.classList.toggle('stor');
      utvid.classList.toggle('aktiv', stor);
      utvid.title = stor ? 'Krymp tidslinja' : 'Utvid tidslinja (mer historikk i ett blikk)';
    };
    logHead.appendChild(utvid);
    // ── AAR (AAR-01) ────────────────────────────────────────────────────────
    // `generateAar()` (@skade/contracts) fantes med tester og null kallsteder —
    // ingen operatør kunne generere en AAR. Tidslinja her ER kilden (samme
    // hendelser som filtreres/sorteres over): knappen sender ALLE hendelsene og
    // «nå» til `onGenererAar`, panelet (main.ts) plukker tidsvindu og mapper.
    const aarBtn = el('button', 'logg-utvid logg-aar');
    aarBtn.textContent = 'AAR';
    aarBtn.title = 'Generer After Action Review fra hendelsene i tidslinja';
    (aarBtn as HTMLButtonElement).onclick = () => this.onGenererAar?.();
    logHead.appendChild(aarBtn);
    d.appendChild(logHead);
    // ── FILTER (UX-rapportens Fase 1, funn F1+F5) ─────────────────────────
    // «Filtrer på type, hastegrad eller enhet.» Linser framfor ni
    // avkryssingsbokser: operatøren velger et PERSPEKTIV, ikke et sett.
    // ⚠ Haste-båndet under filtreres IKKE — se `feltnett.ts`.
    this.linseEl = el('div', 'linse-rad');
    d.appendChild(this.linseEl);
    this.sokEl = document.createElement('input');
    this.sokEl.type = 'search';
    this.sokEl.className = 'linse-sok';
    this.sokEl.placeholder = 'Søk i tidslinja …';
    this.sokEl.oninput = () => {
      this.feltnett = { ...this.feltnett, sok: this.sokEl.value };
      this.tegnFeltnett();
    };
    d.appendChild(this.sokEl);
    // Linja som gjør et filter ærlig: den sier ALLTID hvor mange som er skjult.
    this.filterTekstEl = span('filter-tekst', '');
    d.appendChild(this.filterTekstEl);
    // HASTE-BÅNDET ligger MELLOM overskriften og lista, ikke inni lista: en rad
    // som ruller bort med resten av loggen er ikke et varsel. Sona er tom (og
    // skjult) når ingenting haster, så den koster ingen skjermplass i det
    // normale.
    this.hasteEl = el('div', 'haste-band');
    this.hasteEl.hidden = true;
    d.appendChild(this.hasteEl);
    this.hendelseEl = el('div', 'roster-list hendelse-list');
    d.appendChild(this.hendelseEl);
    // Tom liste ved oppstart. `0` som «nå» er trygt: en tom logg har ingen alder
    // å regne på, og skallet skal ikke lese klokka for noe det ikke viser.
    this.setHendelser([], 0);

    // Levende objektliste (spor + plasserte enheter) øverst — klikk for å fly
    // til / velge. Detaljvisning under.
    const head = el('div', 'roster-head');
    head.append(span('section-label', 'Objekter'), (this.rosterCountEl = span('roster-count', '0')));
    const pop = el('button', 'roster-popout') as HTMLButtonElement;
    pop.textContent = '⧉';
    pop.title = 'Åpne objektlista på egen skjerm';
    pop.onclick = () => this.onPopoutRoster?.();
    head.appendChild(pop);
    d.appendChild(head);
    // Søk i objektlista (eierkrav 2026-08-13: «gjerne søkbart — så jeg kan finne
    // dem enkelt i kartet»). Med alle GNÅ-telefoner i lista blir den lang, og en
    // lang liste uten søk er en liste man slutter å bruke.
    this.rosterSokEl = el('input', 'roster-sok') as HTMLInputElement;
    this.rosterSokEl.type = 'search';
    this.rosterSokEl.placeholder = 'Søk i objekter (navn, nummer, type)';
    this.rosterSokEl.setAttribute('aria-label', 'Søk i objektlista');
    this.rosterSokEl.oninput = () => {
      this.rosterQ = this.rosterSokEl.value;
      this.tegnRoster();
    };
    d.appendChild(this.rosterSokEl);
    this.rosterEl = el('div', 'roster-list');
    d.appendChild(this.rosterEl);
    this.detailEl = el('div', 'detail-body');
    d.appendChild(this.detailEl);
    this.clearDetail();
    return d;
  }

  /**
   * Fyller hendelsesloggen. `naaMs` sendes INN, så alderen regnes mot samme tid
   * for alle rader (og kan testes) — skallet leser ikke klokka for hver rad.
   */
  setHendelser(items: Hendelse[], naaMs: number) {
    this.sisteHendelser = items;
    this.sisteNaa = naaMs;
    this.tegnFeltnett();
  }

  /** Sett linsa utenfra (kommandopalett / hurtigtast). */
  settLinse(l: FeltnettLinse): void {
    this.feltnett = { ...this.feltnett, linse: l };
    this.tegnFeltnett();
  }

  /**
   * Tegner filterrad, haste-bånd og liste av det SISTE datasettet.
   *
   * Skilt fra `setHendelser` fordi et filterbytte ikke er en ny hendelse: ville
   * vi krevd nye data for å bytte linse, måtte flata ventet på neste tikk (opptil
   * 15 sekunder) før klikket fikk effekt.
   */
  private tegnFeltnett() {
    const items = this.sisteHendelser;
    const naaMs = this.sisteNaa;
    const vis = sorterHendelser(filtrerHendelser(items, this.feltnett));
    this.hendelseCountEl.textContent = String(vis.length);
    this.tegnLinser(items);
    const sam = filterSammendrag(items, this.feltnett);
    this.filterTekstEl.textContent = sam.tekst;
    this.filterTekstEl.classList.toggle('advarsel', sam.advarsel);
    // ⚠ HELE settet, ikke det filtrerte: hastegrad som et filter kan skjule er
    // ikke hastegrad. Fase 0 gjorde den synlig; dette holder den synlig.
    this.tegnHasteband(items, naaMs);
    this.hendelseEl.replaceChildren();
    if (!vis.length) {
      // ⚠ TO ULIKE TOMTILSTANDER. «Ingenting har skjedd» og «filteret skjuler
      // alt» er helt forskjellige situasjoner, og å skrive det samme i begge er
      // nøyaktig den likheten som lot samband-buggen stå i dagevis.
      this.hendelseEl.appendChild(span(
        'roster-empty',
        items.length
          ? `Ingen treff — ${items.length} hendelser er skjult av filteret. Trykk «Alt» for å se dem.`
          : 'Ingen hendelser ennå — sendte rapporter, varsler og statusendringer havner her.',
      ));
      return;
    }
    for (const h of vis) this.hendelseEl.appendChild(hendelseRad(h, naaMs));
  }

  /** Chip-raden. Tallet står i chip-en, så operatøren ikke må klikke for å lete. */
  private tegnLinser(items: Hendelse[]) {
    const tell = linseTelling(items, this.feltnett);
    this.linseEl.replaceChildren();
    for (const l of LINSER) {
      const b = el('button', 'linse-chip');
      b.classList.toggle('valgt', this.feltnett.linse === l);
      b.append(span('linse-navn', LINSE_ETIKETT[l]));
      // Tallet vises bare når det er noe der. En «0» på hver ubrukt linse er
      // støy som gjør de tallene som betyr noe vanskeligere å se.
      if (tell[l]) b.append(span('linse-tall', String(tell[l])));
      (b as HTMLButtonElement).onclick = () => this.settLinse(l);
      this.linseEl.appendChild(b);
    }
    // «Nullstill» dukker opp bare når det er noe å nullstille — og den nullstiller
    // ALT, også søkefeltet, for et glemt søkeord er like usynlig som en glemt linse.
    if (erAktivt(this.feltnett)) {
      const nb = el('button', 'linse-chip nullstill');
      nb.textContent = '✕ Nullstill';
      (nb as HTMLButtonElement).onclick = () => {
        this.feltnett = { ...TOMT_FILTER };
        this.sokEl.value = '';
        this.tegnFeltnett();
      };
      this.linseEl.appendChild(nb);
    }
  }

  /**
   * Haste-båndet (UX-funn F8, «plassering øverst og eget varsel»).
   *
   * Regner på HELE settet, ikke på de 60 sorterte radene: en IMMEDIATE som falt
   * ut av visningsvinduet er nettopp den som ikke må forsvinne.
   */
  private tegnHasteband(items: Hendelse[], naaMs: number) {
    const b = hasteBand(items, naaMs);
    this.hasteEl.replaceChildren();
    this.hasteEl.hidden = b.totalt === 0;
    if (!b.totalt) return;
    this.hasteEl.appendChild(span('haste-head', hastebandTekst(b)));
    for (const h of b.vis) this.hasteEl.appendChild(hendelseRad(h, naaMs));
  }

  /** Fyller den levende objektlisten. */
  setRoster(items: RosterItem[]) {
    this.rosterItems = items;
    this.tegnRoster();
  }

  /**
   * Marker hvilken rad som er VALGT. `null` fjerner markeringen.
   *
   * J3-funn 5 («valgt tilstand vises ikke konsekvent») og UX-rapportens «lag 2»:
   * det fantes fem uavhengige «én om gangen»-mekanismer som kunne peke på fem
   * ulike objekter samtidig, og lista viste aldri hvilken rad som var aktiv.
   * Seleksjonen har nå én eier, og lista DERIVERER av den — den holder ikke sin
   * egen mening om hva som er valgt.
   */
  settValgt(id: string | null) {
    if (this.rosterValgt === id) return;
    this.rosterValgt = id;
    this.tegnRoster();
  }

  /** Hvilken rad lista mener er valgt (til test). */
  get valgtRad(): string | null { return this.rosterValgt; }

  /** Tegner lista med gjeldende søk. Skilt fra `setRoster` fordi et tastetrykk i
   * søkefeltet ikke skal kreve at kalleren sender inn alle objektene på nytt. */
  private tegnRoster() {
    const vis = filtrerRoster(this.rosterItems, this.rosterQ);
    // Telleren viser «treff av totalt» under søk. Bare treffene ville sett ut
    // som at objekter forsvant fra situasjonsbildet.
    this.rosterCountEl.textContent = this.rosterQ.trim() && vis.length !== this.rosterItems.length
      ? `${vis.length}/${this.rosterItems.length}`
      : String(this.rosterItems.length);
    if (!this.rosterItems.length) {
      // «Ingen objekter ennå» sa ikke HVA lista inneholder når den er full, og
      // ble derfor lest som at panelet var ødelagt. Nå står det hva som fyller
      // den — og hva operatøren kan gjøre selv (designsystemet §8).
      this.rosterEl.replaceChildren(span(
        'roster-empty',
        'Ingen spor eller plasserte enheter. Lista fylles av SPARK-sensorer, GNÅ-telefoner, deteksjoner og enheter du plasserer (U).',
      ));
      return;
    }
    if (!vis.length) {
      // Viktig skille: «ingenting i lista» og «ingenting som matcher søket» er
      // to ulike tilstander, og å blande dem får operatøren til å tro at sporene
      // er borte fra kartet.
      this.rosterEl.replaceChildren(
        span('roster-empty', `Ingen objekter matcher «${this.rosterQ.trim()}».`),
      );
      return;
    }
    // ⚠ RADENE GJENBRUKES, DE TEGNES IKKE PÅ NYTT.
    //
    // Funnet i ekte Chromium (Playwright, bølge 188): et klikk på en rad traff
    // ingenting. Lista ble bygget med `replaceChildren()` ved hver feed-tikk, og
    // alderen i undertittelen («12 s siden») endrer seg hvert sekund — så raden
    // operatøren siktet på var revet ut av DOM-en i det øyeblikket museknappen
    // gikk ned. Playwright rapporterte det ordrett: «element was detached from
    // the DOM, retrying». En operatør får ingen slik melding; for henne er det
    // bare en liste som ikke svarer.
    //
    // Radene identifiseres derfor på `data-id`, oppdateres på plass, og flyttes
    // bare når rekkefølgen faktisk er endret. Rekkefølgen kommer fra registeret
    // (`TYPE_REKKEFOLGE`) — vi sorterer ikke om her, for da hadde to steder hatt
    // en mening om ordenen.
    const gamle = new Map<string, HTMLElement>();
    for (const el of Array.from(this.rosterEl.children) as HTMLElement[]) {
      const n = el.dataset.nokkel;
      if (n) gamle.set(n, el);
    }
    const ønsket: HTMLElement[] = [];
    let forrigeGruppe: string | null = null;
    for (const it of vis) {
      if (it.gruppe && it.gruppe !== forrigeGruppe) {
        const nøkkel = `g:${it.gruppe}`;
        const h = gamle.get(nøkkel) ?? (() => {
          const ny = span('roster-gruppe', it.gruppe!);
          ny.setAttribute('role', 'presentation');
          ny.dataset.nokkel = nøkkel;
          return ny;
        })();
        ønsket.push(h);
        forrigeGruppe = it.gruppe;
      }
      const nøkkel = `r:${it.id}`;
      const eksisterende = gamle.get(nøkkel);
      ønsket.push(eksisterende
        ? oppdaterRosterRad(eksisterende, it, this.rosterValgt === it.id)
        : rosterRow(it, this.rosterValgt === it.id));
    }
    // Fjern det som ikke skal være her lenger, og sett resten i riktig orden.
    // `insertBefore` med et element som alt står riktig er en no-op i praksis —
    // nettleseren flytter ikke fokus eller avbryter et pågående klikk da.
    for (const [nøkkel, el] of gamle) {
      if (!ønsket.some((x) => x.dataset.nokkel === nøkkel)) el.remove();
    }
    let neste = this.rosterEl.firstElementChild;
    for (const el of ønsket) {
      if (el === neste) { neste = neste.nextElementSibling; continue; }
      this.rosterEl.insertBefore(el, neste);
    }
  }

  private tickClock() {
    const now = new Date();
    this.zuluEl.textContent = now.toISOString().slice(11, 19) + 'Z';
    this.localEl.textContent = now.toLocaleTimeString('nb-NO', { hour12: false });
  }

  setCount(id: string, n: number) {
    if (this.counts[id]) this.counts[id].textContent = String(n);
  }

  /** Sist kjente avlesninger, så nedre rad kan bytte kilde uten å miste verdien. */
  private mouseMgrs = '';
  private crosshairMgrs = '';

  /** MGRS under musepekeren (bakoverkompatibelt alias). */
  setCoord(text: string) { this.setMouseCoord(text); }

  // MGRS FORMATERES HER, IKKE PÅ HVERT KALLSTED. Kallerne sender kompakt MGRS
  // rett fra `mgrsForward`; grupperingen hører til visningen. Ett sted betyr at
  // ingen ny avlesning kan bli glemt — eier-bestillingen var mellomrom «mellom
  // 1 og 5», altså mellom easting og northing, og den skal gjelde overalt.
  setMouseCoord(text: string) {
    this.mouseMgrs = formatMgrs(text || '');
    this.renderLowerCoord();
  }

  /** MGRS i trådkorset (kartsenter). Vises i stedet for MUS når trådkorset er på. */
  setCrosshairCoord(text: string) {
    this.crosshairMgrs = formatMgrs(text || '');
    this.renderLowerCoord();
  }

  setSelfCoord(text: string) {
    const el2 = this.coordEl.querySelector('[data-self]');
    if (el2) el2.textContent = text ? formatMgrs(text) : '—';
  }

  /** Nedre rad: TRÅDKORS når sikte er aktivt, ellers MUS. Etikett følger verdien. */
  private renderLowerCoord() {
    const { label, value } = lowerCoordRow(this.isCrosshairOn(), this.mouseMgrs, this.crosshairMgrs);
    const lbl = this.coordEl.querySelector('[data-lbl2]');
    const val = this.coordEl.querySelector('[data-mouse]');
    if (lbl) lbl.textContent = label;
    if (val) val.textContent = value;
  }

  /** Slår tråkorset av/på. Returnerer ny tilstand. Valget huskes. */
  toggleCrosshair(): boolean {
    const on = this.crosshairEl.classList.toggle('on');
    skrivTrakorsValg(on);
    this.renderLowerCoord(); // bytt nedre rad MUS <-> TRÅDKORS umiddelbart
    return on;
  }

  /** Sett tråkorset til en gitt tilstand (til avkryssing i kartmenyen). */
  setCrosshair(on: boolean): boolean {
    this.crosshairEl.classList.toggle('on', on);
    skrivTrakorsValg(on);
    this.renderLowerCoord();
    return on;
  }

  isCrosshairOn(): boolean { return this.crosshairEl.classList.contains('on'); }

  showAlarm(text: string) {
    this.alarmEl.innerHTML = '';
    this.alarmEl.append(document.createTextNode(text));
    const btn = el('button') as HTMLButtonElement;
    btn.textContent = 'GÅ TIL';
    btn.onclick = () => this.onGoto();
    this.alarmEl.appendChild(btn);
    this.alarmEl.classList.add('show');
    haptic(HAPTIC.alarm); // taktilt varsel (undertrykt i EMCON)
    setTimeout(() => this.alarmEl.classList.remove('show'), 8000);
  }

  /**
   * TILBUD: en konsekvensrik handling appen KAN gjøre, men som operatøren må
   * velge. Ingen av valgene skjer av seg selv, og forsvinner stripa uten at noe
   * ble trykket, er utfallet «ingenting» — det trygge utfallet.
   *
   * HVORFOR DEN FINNES: alternativet var å bare GJØRE handlingen og sette et
   * varsel om at den var gjort (slik forflytningsordren ble skrevet automatisk
   * ved dragning). Da er operatøren redusert til å rydde opp etter appen.
   * `showAlarm` kan ikke brukes til dette — den har én fast «GÅ TIL»-knapp og er
   * et VARSEL om noe som har skjedd, ikke et spørsmål.
   */
  showTilbud(
    tekst: string,
    valg: Array<{ label: string; run: () => void; primar?: boolean }>,
    msLevetid = 20_000,
  ) {
    if (this.tilbudTimer !== null) { clearTimeout(this.tilbudTimer); this.tilbudTimer = null; }
    this.tilbudEl.replaceChildren();
    this.tilbudEl.append(span('tilbud-tekst', tekst));
    const lukk = () => {
      this.tilbudEl.classList.remove('show');
      if (this.tilbudTimer !== null) { clearTimeout(this.tilbudTimer); this.tilbudTimer = null; }
    };
    for (const v of valg) {
      const b = el('button', v.primar ? 'tilbud-btn primar' : 'tilbud-btn') as HTMLButtonElement;
      b.type = 'button';
      b.textContent = v.label;
      b.onclick = () => { lukk(); v.run(); };
      this.tilbudEl.append(b);
    }
    const x = el('button', 'tilbud-x') as HTMLButtonElement;
    x.type = 'button'; x.textContent = '×'; x.title = 'Lukk — ingenting gjøres';
    x.onclick = lukk;
    this.tilbudEl.append(x);
    this.tilbudEl.classList.add('show');
    this.tilbudTimer = window.setTimeout(lukk, msLevetid);
  }

  clearDetail() {
    this.detailEl.innerHTML = '<div class="detail-empty">Velg et objekt på kartet</div>';
  }

  /** Kalles når operatøren ber om å slippe et spor («drop track»). Settes av main. */
  onDroppSpor?: (t: Track) => void;
  /**
   * Åpne IMINT-analyse på et observasjonsbilde. Settes av `main.ts`; er den
   * usatt, vises bildet uten analyse-knapp — framfor en knapp som ikke gjør noe.
   */
  onImint?: (t: Track, bildeUrl: string) => void;
  /**
   * TRÅDEN PÅ OBJEKTET (UX-funn **F4**, HØY: «chatten lever adskilt fra kartet»).
   *
   * `main.ts` setter denne. `sammendrag` er teksten fra
   * `objektTraadSammendrag()` i contracts (én kilde, §3), `antall` styrer om
   * panelet skal vise en «åpne»-knapp i det hele tatt, `onMeld` starter en ny
   * melding forankret i sporet, og `onApne` viser samtalen som alt finnes.
   *
   * Er hooken usatt, bygges seksjonen ikke — framfor en knapp som ikke gjør noe.
   */
  onSporTraad?: (t: Track) => { sammendrag: string; antall: number; onMeld: () => void; onApne: () => void };

  showDetail(t: Track) {
    // aff er en fast enum-verdi (friend/hostile/…), trygg i klassenavn.
    const aff = t.affiliation;
    const naa = Date.now();
    const ferskhet = t.ferskhetMs && t.ferskhetMs > 0 ? t.ferskhetMs : 300_000;
    const gammelt = t.tsMs !== 0 && naa - t.tsMs > ferskhet;
    const uplausibelt = t.extra?.[PLAUSIBILITET_NOKKEL];
    const rows = sporRader(t, naa, gammelt);

    // SIKKERHET: label/source/kind/extra kan stamme fra en angriperstyrt
    // sensor-payload (uautentisert ingest). ALT felt-avledet escapes før DOM
    // for å hindre stored XSS i C2-klienten.
    const badge = t.estimated
      ? '<span class="badge est">ESTIMERT</span>'
      : `<span class="badge ${aff}">${esc(aff.toUpperCase())}</span>`;
    // «SISTE KJENTE POSISJON» er et eget merke, ikke en nyanse i grått. For et
    // bevart spor er dette den viktigste opplysningen i panelet: enheten har
    // sluttet å rapportere, og dette er der den var.
    const gammeltMerke = gammelt && t.bevares
      ? `<span class="badge sist">SISTE KJENTE · ${esc(alderTekst(naa, t.tsMs))}</span>`
      : '';
    // Forbeholdet gjentas som merke i undertittelen, der ESTIMERT og SISTE
    // KJENTE alt bor. Den linja ER panelets tillitslinje, og «punktet er ikke
    // troverdig» hører i samme familie som «dette er et estimat».
    const uplausibeltMerke = uplausibelt
      ? '<span class="badge uplausibel">⚠ USIKKER POSISJON</span>'
      : '';
    this.detailEl.innerHTML =
      `<div class="detail-title">${esc(t.label)}</div>` +
      `<div class="detail-sub">${esc(t.kind.toUpperCase())} ${badge}${gammeltMerke}${uplausibeltMerke}</div>` +
      rows
        .map(([k, v]) => `<div class="kv"><span class="k">${esc(k)}</span><span class="v">${esc(v)}</span></div>`)
        .join('');
    // ── BILDET, som et BILDE ─────────────────────────────────────────────────
    //
    // Eierfunn 2026-08-17: «bildet kommer ikke». Bildet KOM — men detaljpanelet
    // rendret `extra` som tekstrader, så etterretningsanalytikeren fikk se
    // strengen «/media/a3f9….jpg». Jeg meldte det som ferdig i bølge 194–195.
    // Det var det ikke, og dette lukker det.
    //
    // Bygges som DOM og ikke innerHTML: `src` settes via egenskap, aldri via
    // interpolert HTML, så en feltavledet URL ikke kan bryte ut av attributtet.
    const bildeUrl = t.extra?.[BILDE_EXTRA_NOKKEL];
    if (bildeUrl) {
      const fig = el('figure', 'detail-bilde');
      const img = document.createElement('img');
      // Samme guard som IMINT-annotatoren: bare samme opphav, https eller
      // data:image. Et delt produkt skal ikke kunne beacone til en angriper-URL.
      img.src = trygtBildeUrl(bildeUrl);
      img.alt = 'Observasjonsbilde';
      img.loading = 'lazy';
      img.title = 'Klikk for IMINT-analyse';
      fig.appendChild(img);
      this.detailEl.appendChild(fig);

      // IMINT: analytikeren tegner merkede bokser på bildet. Modulen
      // (`ui/imint.ts`) har ligget ferdig og ubrukt — dette er første kallsted.
      if (this.onImint) {
        const analyser = el('button', 'detail-imint') as HTMLButtonElement;
        analyser.type = 'button';
        analyser.textContent = 'IMINT-analyse';
        analyser.title = 'Merk objekter i bildet og lagre til etterretningsproduktet';
        analyser.onclick = () => this.onImint?.(t, bildeUrl);
        this.detailEl.appendChild(analyser);
        img.style.cursor = 'zoom-in';
        img.onclick = () => this.onImint?.(t, bildeUrl);
      }
    }

    // «Slipp spor» — den ENESTE veien ut av bildet for et bevart spor, og den
    // går gjennom et menneske. Knappen bygges som DOM (ikke innerHTML) fordi den
    // bærer en handler, og den vises bare for bevarte spor: et deteksjonsspor
    // forsvinner av seg selv, og en knapp som ikke gjør noe varig er verre enn
    // ingen knapp.
    // ── TRÅDEN PÅ SPORET (funn F4) ─────────────────────────────────────────
    // «Å melde om fiendtlig kjøretøy skjer PÅ kjøretøyet, ikke i en løsrevet
    // kanal.» Seksjonen står FØR «Slipp spor» med vilje: å melde om et spor er
    // det operatøren gjør ofte, å slippe det er noe hun gjør én gang.
    if (this.onSporTraad) {
      const tr = this.onSporTraad(t);
      const boks = el('div', 'spor-traad');
      // ⚠ SAMMENDRAGET STÅR ALLTID, også når det er null meldinger. Tomtilstanden
      // er selve invitasjonen: uten den ser sporet ut som noe man ikke kan snakke
      // om.
      boks.append(span('spor-traad-sum', tr.sammendrag));
      const rad = el('div', 'detail-handlinger');
      const meld = el('button', 'targetlist-btn') as HTMLButtonElement;
      meld.type = 'button';
      meld.textContent = '✎ Meld om dette sporet';
      meld.title = 'Ny stridsmelding hengt på dette sporet — den følger objektet, ikke en kanal';
      meld.onclick = () => tr.onMeld();
      rad.append(meld);
      if (tr.antall > 0) {
        const apne = el('button', 'targetlist-btn') as HTMLButtonElement;
        apne.type = 'button';
        apne.textContent = `Åpne samtalen (${tr.antall})`;
        apne.onclick = () => tr.onApne();
        rad.append(apne);
      }
      boks.append(rad);
      this.detailEl.appendChild(boks);
    }
    if (t.bevares && this.onDroppSpor) {
      const knapp = el('button', 'detail-dropp') as HTMLButtonElement;
      knapp.type = 'button';
      knapp.textContent = 'Slipp spor';
      knapp.title = 'Fjern siste kjente posisjon fra situasjonsbildet — for alle operatører';
      knapp.onclick = () => this.onDroppSpor?.(t);
      this.detailEl.appendChild(knapp);
    }
  }

  /**
   * Detaljpanelet for ALT ANNET enn spor: kritiske objekter, OSM-infrastruktur,
   * SKANDIA-enheter, hendelser i loggen.
   *
   * FUNN 06: et kartklikk ga en svevende boble ved klikkpunktet mens
   * høyrepanelet — som er BYGGET for detaljer — sto tomt. Boblen dekker kartet
   * der operatøren nettopp pekte, forsvinner ved neste klikk, og kan ikke
   * sammenlignes med noe. Detaljene hører hjemme her; boblen er nå bare en kort
   * bekreftelse på HVA som ble truffet.
   *
   * `handlinger` er valgfri: knapper som hører til objektet (rediger, flytt).
   * De legges under radene, ikke inni boblen, av samme grunn.
   */
  showDetailRows(
    tittel: string,
    undertittel: string,
    rows: Array<[string, string]>,
    handlinger?: Array<{ label: string; run: () => void }>,
  ) {
    this.detailEl.replaceChildren();
    const t = el('div', 'detail-title');
    t.textContent = tittel; // feltavledet tekst → textContent, aldri innerHTML
    const s = el('div', 'detail-sub');
    s.textContent = undertittel;
    this.detailEl.append(t, s);
    for (const [k, v] of rows) {
      if (!v) continue;
      const row = el('div', 'kv');
      row.append(span('k', k), span('v', v));
      this.detailEl.append(row);
    }
    if (handlinger?.length) {
      const rad = el('div', 'detail-handlinger');
      for (const h of handlinger) {
        const b = el('button', 'targetlist-btn') as HTMLButtonElement;
        b.type = 'button';
        b.textContent = h.label;
        b.onclick = () => h.run();
        rad.append(b);
      }
      this.detailEl.append(rad);
    }
  }
}

/**
 * Én rad i hendelsesloggen: type-prikk, tittel, alder til høyre.
 *
 * Alderen står ALLTID der — en logglinje uten alder er en påstand uten
 * holdbarhetsdato, og «AMMO RØD» leses helt annerledes hvis den er fra i går.
 */
function hendelseRad(h: Hendelse, naaMs: number): HTMLElement {
  const rad = el(h.onClick ? 'button' : 'div', 'roster-row hendelse-rad');
  // Hastegrads-stripe på VENSTRE KANT av raden. Klasse, ikke inline farge, så
  // tokenet bor i `style.css` (designsystemet UX-022).
  const kl = hastegradKlasse(h);
  if (kl) rad.classList.add('haste', kl);
  // FORELDET (eierbeslutning: grå etter et døgn). ⚠ Bare dempet farge — raden
  // beholder plassen sin i tidslinja og klikket sitt. En foreldet hendelse er
  // fortsatt en hendelse; det er ferskheten vi ikke lenger står for.
  if (h.foreldet) rad.classList.add('foreldet');
  const dot = el('span', 'roster-dot');
  if (h.farge) dot.style.background = h.farge;
  const midt = el('div', 'hendelse-midt');
  midt.append(span('hendelse-tittel', h.tittel));
  // KODEORDET STÅR SOM TEKST, ikke bare som farge. En stripe alene er ubrukelig
  // for en fargeblind operatør, og umulig å lese av på en skjerm i solskinn med
  // nattfilter på.
  const detalj = h.hastegrad
    ? `${PRECEDENCE_CODEWORD[h.hastegrad]} · ${HENDELSE_ETIKETT[h.type]} · ${h.detalj}`
    : `${HENDELSE_ETIKETT[h.type]} · ${h.detalj}`;
  midt.append(span('hendelse-detalj', detalj));
  const alder = span('hendelse-alder', alderTekst(naaMs, h.tsMs));
  alder.title = new Date(h.tsMs).toISOString().slice(11, 19) + 'Z';
  rad.append(dot, midt, alder);
  if (h.onClick) (rad as HTMLButtonElement).onclick = () => h.onClick?.();
  return rad;
}

/** HTML-escaper — brukes på ALL felt-avledet tekst før innerHTML-innsetting. */
function esc(s: string): string {
  return String(s)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

/**
 * Bygger én liste-rad (objektliste + meldingsliste deler denne).
 *
 * Raden er en `div` når den har en fjern-knapp: en `button` inne i en `button` er
 * ugyldig HTML, og nettleseren flytter da knappen UT av raden — hvorpå
 * papirkorgen havner på egen linje og treffer ingenting.
 */
function rosterRow(it: RosterItem, valgt = false): HTMLElement {
  const harSlett = typeof it.onSlett === 'function';
  const row = el(harSlett ? 'div' : 'button', 'roster-row');
  row.dataset.id = it.id;
  row.dataset.nokkel = `r:${it.id}`;
  const dot = el('span', 'roster-dot');
  dot.dataset.del = 'dot';
  const name = span('roster-name', it.label);
  name.dataset.del = 'navn';
  const kind = span('roster-kind', it.sub);
  kind.dataset.del = 'sub';
  if (harSlett) {
    // Selve raden må fortsatt være tastaturnåbar når den ikke er en `button`.
    row.setAttribute('role', 'button');
    row.tabIndex = 0;
    row.onkeydown = (e) => {
      const k = (e as KeyboardEvent).key;
      if (k === 'Enter' || k === ' ') { e.preventDefault(); it.onClick(); }
    };
  }
  row.append(dot, name, kind);
  if (harSlett) {
    // PAPIRKORG, ikke `×`. `×` betyr LUKK overalt ellers i COP-en, og den samme
    // krysseformen betydde tidligere «slett for godt» i noen paneler og «skjul
    // kortet» i andre. Én form, én betydning (UX-rapporten §4b).
    const slett = el('button', 'roster-slett') as HTMLButtonElement;
    slett.type = 'button';
    slett.dataset.del = 'slett';
    slett.innerHTML = icon('trash');
    row.appendChild(slett);
  }
  return oppdaterRosterRad(row, it, valgt);
}

/**
 * Oppdater en EKSISTERENDE rad på plass.
 *
 * Alt som kan endres mellom to tegninger settes her, og bare her: tekst, farge,
 * tilhørighet, valgt-tilstand og handlerne (som lukker over ferske `Kartobjekt`-
 * håndtak og derfor MÅ byttes, ellers slettet knappen et objekt fra forrige
 * tikk). DOM-noden er den samme, så et klikk som er i gang ikke avbrytes.
 */
function oppdaterRosterRad(row: HTMLElement, it: RosterItem, valgt: boolean): HTMLElement {
  row.className = `roster-row aff-${it.aff}${valgt ? ' valgt' : ''}`;
  if (valgt) row.setAttribute('aria-current', 'true');
  else row.removeAttribute('aria-current');
  const del = (navn: string) => row.querySelector<HTMLElement>(`[data-del="${navn}"]`);
  const dot = del('dot');
  if (dot) dot.style.background = it.color ?? '';
  const navn = del('navn');
  if (navn) navn.textContent = it.label;
  const sub = del('sub');
  if (sub) sub.textContent = it.sub;
  const slett = del('slett');
  if (slett) {
    const tittel = it.slettTittel ?? `Slett ${it.label}`;
    slett.title = tittel;
    slett.setAttribute('aria-label', tittel);
    slett.onclick = (e) => { e.stopPropagation(); it.onSlett?.(); };
  }
  row.onclick = () => it.onClick();
  return row;
}

function el(tag: string, cls?: string): HTMLElement {
  const e = document.createElement(tag);
  if (cls) e.className = cls;
  return e;
}
function span(cls: string, text: string): HTMLElement {
  const s = el('span', cls);
  s.textContent = text;
  return s;
}
function label(text: string): HTMLElement {
  return span('section-label', text);
}
/**
 * Én RBAC-rad. Formen er bevisst ULIK `.layer-row`: hengelås (lukket/åpen) i
 * stedet for status-prikk, tekstlig TILLATT/SPERRET i stedet for ✓/—, og ingen
 * bryter-affordans (se kommentaren i `sidebar()`). «Informasjon» skal ikke være
 * kledd som «kontroll».
 */
export function permRow(name: string, on: boolean): HTMLElement {
  const row = el('div', on ? 'perm-row' : 'perm-row off');
  const lock = el('span', 'perm-lock');
  // Klarert konstant fra icons.ts (ren SVG-streng), ingen feltdata.
  lock.innerHTML = icon(on ? 'lock-open' : 'lock');
  row.append(lock, span('perm-name', name), span('perm-state', on ? 'TILLATT' : 'SPERRET'));
  row.title = `${name}: ${on ? 'tillatt' : 'sperret'} for din rolle og organisasjon `
    + '(RBAC) — håndheves i backend, kan ikke endres her.';
  return row;
}
function cap(s: string): string {
  return s.charAt(0).toUpperCase() + s.slice(1);
}
/** «@ola.nordmann:skade» → «ola.nordmann» for kompakt visning. */
function shortId(id: string): string {
  return id.replace(/^@/, '').replace(/:.*$/, '');
}

interface MenuItem { label: string; glyph: string; run: () => void; danger?: boolean }

/** Liten nedtrekksmeny: knapp + panel. Kun én åpen om gangen; lukkes ved
 * klikk utenfor / Escape. `trigger` er innholdet i knappen. */
function dropdown(btnClass: string, trigger: HTMLElement, items: MenuItem[]): HTMLElement {
  const wrap = el('div', 'hdr-menu');
  const btn = el('button', btnClass) as HTMLButtonElement;
  btn.type = 'button';
  btn.append(trigger, span('menu-caret', '▾'));
  const panel = el('div', 'hdr-menu-panel');
  for (const it of items) {
    const b = el('button', it.danger ? 'hdr-menu-item danger' : 'hdr-menu-item') as HTMLButtonElement;
    b.append(span('mi-glyph', it.glyph), span('mi-label', it.label));
    b.onclick = (e) => { e.stopPropagation(); panel.classList.remove('open'); it.run(); };
    panel.appendChild(b);
  }
  btn.onclick = (e) => {
    e.stopPropagation();
    const wasOpen = panel.classList.contains('open');
    document.querySelectorAll('.hdr-menu-panel.open').forEach((p) => p.classList.remove('open'));
    if (!wasOpen) panel.classList.add('open');
  };
  document.addEventListener('click', () => panel.classList.remove('open'));
  document.addEventListener('keydown', (e) => { if (e.key === 'Escape') panel.classList.remove('open'); });
  wrap.append(btn, panel);
  return wrap;
}

/**
 * Bekreftelse FØR en handling som ikke kan trekkes tilbake.
 *
 * Bygget som modal og ikke `window.confirm` av samme grunn som navnefeltet i
 * enhets-popupen ikke er `window.prompt`: en nettleser-dialog kan ikke stiles,
 * blokkerer hele vinduet, og oppfører seg ulikt i pop-out-vinduer (der SKADE
 * faktisk kjører flere paneler).
 *
 * ⚠ ASYMMETRI, som i 112-knappen: friksjonen ligger på Å BEGYNNE. Å slutte
 * (skru av) skal aldri kreve dette. Standardfokus står på «Avbryt», slik at en
 * Enter fra en operatør som ikke leste ferdig IKKE bekrefter.
 */
export function bekreft(opts: {
  tittel: string;
  tekst: string;
  /** Punkter som gjør konsekvensen konkret («Posisjonen deles til …»). */
  punkter?: string[];
  bekreftTekst: string;
  onBekreft: () => void;
  avbrytTekst?: string;
}): void {
  const body = el('div', 'modal-body');
  const t = span('bekreft-tekst', opts.tekst);
  body.append(t);
  if (opts.punkter?.length) {
    const ul = el('ul', 'bekreft-punkter');
    for (const p of opts.punkter) {
      const li = el('li');
      li.textContent = p; // feltavledet tekst: aldri innerHTML
      ul.append(li);
    }
    body.append(ul);
  }
  const rad = el('div', 'bekreft-handlinger');
  const avbryt = el('button', 'bekreft-avbryt') as HTMLButtonElement;
  avbryt.type = 'button';
  avbryt.textContent = opts.avbrytTekst ?? 'Avbryt';
  const ok = el('button', 'bekreft-ok') as HTMLButtonElement;
  ok.type = 'button';
  ok.textContent = opts.bekreftTekst;
  rad.append(avbryt, ok);
  body.append(rad);
  const lukk = modalEl(opts.tittel, body);
  avbryt.onclick = () => lukk();
  ok.onclick = () => { lukk(); opts.onBekreft(); };
  avbryt.focus();
}

/** Enkel modal med etikett/verdi-rader. */
function modal(title: string, rows: Array<[string, string]>) {
  const body = el('div', 'modal-body');
  for (const [k, v] of rows) {
    const row = el('div', 'kv');
    row.append(span('k', k), span('v', v));
    body.appendChild(row);
  }
  modalEl(title, body);
}

/**
 * Modal med vilkårlig innhold. Returnerer en lukk-funksjon.
 *
 * `onClose` (valgfri) kalles HVER gang modalen lukkes — via X, Escape, klikk
 * utenfor, ELLER et eksplisitt `lukk()`-kall fra kalleren selv. `sporTekst()`
 * bruker den til å svare `null` («avbrutt») uansett HVORDAN operatøren lukket
 * dialogen, uten å måtte doble opp lytting på alle fire veiene selv.
 */
function modalEl(title: string, body: HTMLElement, onClose?: () => void): () => void {
  document.querySelector('.skade-modal-overlay')?.remove();
  const overlay = el('div', 'skade-modal-overlay');
  const box = el('div', 'skade-modal');
  const head = el('div', 'modal-head');
  head.append(span('modal-title', title));
  const x = el('button', 'report-x') as HTMLButtonElement;
  x.textContent = '×'; x.onclick = () => lukk();
  head.append(x);
  box.append(head, body);
  overlay.append(box);
  // Escape og klikk utenfor AVBRYTER (fjerner uten å utføre) — den trygge veien
  // ut skal alltid være den enkleste.
  const paaTast = (e: KeyboardEvent) => { if (e.key === 'Escape') lukk(); };
  const lukk = () => {
    document.removeEventListener('keydown', paaTast);
    overlay.remove();
    onClose?.();
  };
  document.addEventListener('keydown', paaTast);
  overlay.onclick = (e) => { if (e.target === overlay) lukk(); };
  document.body.appendChild(overlay);
  return lukk;
}

/**
 * Fritekst inn — inline erstatning for `window.prompt`.
 *
 * Samme grunn som `bekreft()` erstatter `window.confirm` (se kommentaren der):
 * en nettleser-dialog kan ikke stiles, blokkerer hele vinduet, og oppfører seg
 * ulikt i pop-out-vinduer.
 *
 * `onSvar` kalles ALLTID nøyaktig én gang: med feltets rå verdi (ikke trimmet
 * — kalleren avgjør selv, akkurat som med `window.prompt`s returverdi) ved
 * bekreft, og med `null` ved avbryt — enten via «Avbryt»-knappen, ×, Escape
 * eller klikk utenfor. Det speiler nøyaktig hva `window.prompt` returnerte
 * («verdi» vs. `null`) slik at forretningslogikken på kallestedet er urørt.
 */
export function sporTekst(opts: {
  tittel: string;
  /** Forklarende tekst over feltet — kan være flerlinjet, som i window.prompt. */
  tekst?: string;
  placeholder?: string;
  verdi?: string;
  /** Fritekst over flere linjer (textarea) i stedet for ett enkelt felt. */
  flerlinje?: boolean;
  bekreftTekst?: string;
  avbrytTekst?: string;
  onSvar: (verdi: string | null) => void;
}): void {
  const body = el('div', 'modal-body');
  if (opts.tekst) {
    const t = span('bekreft-tekst', opts.tekst);
    t.style.whiteSpace = 'pre-wrap';
    body.append(t);
  }
  const felt = document.createElement(opts.flerlinje ? 'textarea' : 'input') as HTMLInputElement | HTMLTextAreaElement;
  felt.className = 'report-input';
  if (!opts.flerlinje) (felt as HTMLInputElement).type = 'text';
  felt.value = opts.verdi ?? '';
  if (opts.placeholder) felt.setAttribute('placeholder', opts.placeholder);
  body.append(felt);
  const rad = el('div', 'bekreft-handlinger');
  const avbryt = el('button', 'bekreft-avbryt') as HTMLButtonElement;
  avbryt.type = 'button';
  avbryt.textContent = opts.avbrytTekst ?? 'Avbryt';
  const ok = el('button', 'bekreft-ok') as HTMLButtonElement;
  ok.type = 'button';
  ok.textContent = opts.bekreftTekst ?? 'OK';
  rad.append(avbryt, ok);
  body.append(rad);
  let ferdig = false;
  const lukk = modalEl(opts.tittel, body, () => { if (!ferdig) { ferdig = true; opts.onSvar(null); } });
  const send = () => { ferdig = true; lukk(); opts.onSvar(felt.value); };
  avbryt.onclick = () => lukk();
  ok.onclick = () => send();
  felt.onkeydown = (e) => {
    if (e.key === 'Enter' && !opts.flerlinje) { e.preventDefault(); send(); }
  };
  setTimeout(() => { felt.focus(); if (felt instanceof HTMLInputElement) felt.select(); }, 0);
}
